import { useQuery } from '@tanstack/react-query'

import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Blank, Panel, Row } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip, Dot, type Tone } from '../components/ui/tone'
import { api, type Deliverability, type HealthState } from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Deliverability (§16, §35, §38, §43).
 *
 * **There is no overall score on this screen, and there must never be one.** §43 forbids inventing
 * a single universal reputation number, and the reason is practical rather than doctrinal: the
 * inputs come from sources that measure different things at different times, four of the five are
 * not connected yet, and a green figure computed from one live source would be the most reassuring
 * and least true thing this console could display.
 *
 * So each source is listed separately, including the ones that are silent — with what blocks them.
 * An empty panel reads as "nothing wrong"; the truth is "nothing is measuring", and those are
 * different sentences.
 */

const HEALTH_TONE: Record<HealthState, Tone> = {
  HEALTHY: 'ok',
  WARNING: 'warn',
  BROKEN: 'bad',
  // Grey, and this is the one that matters: "not checked" must never look like "checked and fine".
  UNKNOWN: 'neutral',
}

const HEALTH_LABEL: Record<HealthState, string> = {
  HEALTHY: 'healthy',
  WARNING: 'warning',
  BROKEN: 'broken',
  UNKNOWN: 'not checked',
}

const BOUNCE_LABEL: Record<string, string> = {
  HARD: 'Hard — the address does not exist',
  SOFT: 'Soft — temporary, retryable',
  BLOCK: 'Blocked by the receiver',
  SPAM: 'Rejected as spam',
  POLICY: 'Refused by policy',
  TECHNICAL: 'Technical failure',
  UNKNOWN: 'Unclassified',
}

export function DeliverabilityScreen() {
  const { can } = useSession()

  const data = useQuery({
    queryKey: ['deliverability'],
    queryFn: () => api.get<Deliverability>('/deliverability'),
    enabled: can('view_contacts'),
  })

  if (!can('view_contacts')) {
    return (
      <Screen>
        <ScreenHeader title="Deliverability" description="Whether mail from here would arrive." />
        <Panel>
          <Blank icon="denied" title="You do not have access to deliverability" />
        </Panel>
      </Screen>
    )
  }

  if (data.isLoading) {
    return (
      <Screen>
        <ScreenHeader title="Deliverability" description="Loading…" />
      </Screen>
    )
  }

  if (data.isError || !data.data) {
    return (
      <Screen>
        <ScreenHeader title="Deliverability" />
        <Panel>
          <Blank
            icon="error"
            tone="bad"
            title="Deliverability could not be read"
            action={<Button onClick={() => void data.refetch()}>Try again</Button>}
          />
        </Panel>
      </Screen>
    )
  }

  const d = data.data
  const totalBounces = d.bounces.reduce((sum, row) => sum + row.count, 0)

  return (
    <Screen>
      <ScreenHeader
        title="Deliverability"
        description={`Authentication, bounces, complaints and reputation signals over the last ${d.windowDays} days. No single score — §43 forbids one, and four of the five sources are not connected yet.`}
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Domain authentication" description="Queried against public DNS, not a dashboard">
          {d.domains.length === 0 ? (
            <Blank
              icon="domains"
              title="No domains are registered."
              body="Authentication is the one part of deliverability that can be got right before anything sends."
            />
          ) : (
            <Table>
              <THead>
                <Th>Domain</Th>
                <Th width={130}>Health</Th>
                <Th width={100} align="right">
                  Mailboxes
                </Th>
              </THead>
              <TBody>
                {d.domains.map((domain) => (
                  <Tr key={domain.id}>
                    <Td mono>{domain.fqdn}</Td>
                    <Td>
                      <span className="flex items-center gap-2">
                        <Dot tone={HEALTH_TONE[domain.health]} label={HEALTH_LABEL[domain.health]} />
                        <span className="text-muted">{HEALTH_LABEL[domain.health]}</span>
                      </span>
                    </Td>
                    <Td align="right" className="tabular-nums">
                      {domain._count.mailboxes}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </Panel>

        <Panel
          title="Bounces and complaints"
          description="By class, because a hard bounce and a soft one mean opposite things"
        >
          {totalBounces === 0 && d.complaints === 0 ? (
            <Blank
              icon="empty"
              title="Nothing has bounced and nobody has complained."
              body="Nothing has been sent either — this is a real zero, not a healthy one."
            />
          ) : (
            <div>
              {d.bounces.map((row) => (
                <Row key={row.bounceClass} label={BOUNCE_LABEL[row.bounceClass] ?? row.bounceClass}>
                  <span className="tabular-nums">{row.count.toLocaleString()}</span>
                </Row>
              ))}
              <Row label="Spam complaints">
                <span className="tabular-nums">{d.complaints.toLocaleString()}</span>
              </Row>
            </div>
          )}
        </Panel>
      </div>

      {/* §43's own rule, made visible. Naming the silent sources is the point of the panel. */}
      <Panel
        title="Where the signals come from"
        description="§43 keeps each source separately attributed rather than averaging them into a score"
      >
        <Table>
          <THead>
            <Th width={150}>Source</Th>
            <Th width={110}>Status</Th>
            <Th>What it measures, or what blocks it</Th>
          </THead>
          <TBody>
            {d.sources.map((source) => (
              <Tr key={source.source}>
                <Td>{source.source}</Td>
                <Td>
                  {source.live ? (
                    <Chip tone="ok">live</Chip>
                  ) : (
                    <Chip tone="neutral">not connected</Chip>
                  )}
                </Td>
                <Td className="text-muted">{source.note}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </Panel>

      {d.signals.length > 0 && (
        <Panel title="Recorded signals" description={`Since ${new Date(d.since).toLocaleDateString()}`}>
          <Table>
            <THead>
              <Th width={130}>Source</Th>
              <Th>Metric</Th>
              <Th width={110} align="right">
                Value
              </Th>
              <Th width={130}>Window</Th>
            </THead>
            <TBody>
              {d.signals.map((signal) => (
                <Tr key={signal.id}>
                  <Td>{signal.source}</Td>
                  <Td className="text-muted">{signal.metric}</Td>
                  <Td align="right" className="tabular-nums">
                    {signal.value === null ? '—' : signal.value}
                  </Td>
                  <Td className="text-muted">
                    {new Date(signal.windowDate).toLocaleDateString()}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </Panel>
      )}
    </Screen>
  )
}
